import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "phosphor-react";
import { updateSeoMetadata } from "../lib/seo";

export default function Terms() {
  const navigate = useNavigate();

  useEffect(() => {
    updateSeoMetadata({
      title: "Terms of Use | GENI",
      description: "Read the Terms of Use for GENI, the trading analytics and journaling platform.",
      path: "/terms",
      lang: "en",
    });
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] p-6 md:p-12" data-testid="terms-page">
      <div className="w-full max-w-[680px] mx-auto">
        
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-8 group"
          data-testid="terms-back-button"
        >
          <ArrowLeft size={20} weight="bold" className="group-hover:-translate-x-1 transition-transform" />
          <span className="text-sm font-medium">Back</span>
        </button>

        {/* Header */}
        <header className="mb-12">
          <h1 className="text-4xl font-bold text-white mb-2">Terms of Use</h1>
          <p className="text-white/40 text-sm">Last updated: March 2026</p>
        </header>

        {/* Content */}
        <div className="space-y-8 text-white/70 leading-relaxed">
          
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">1. Acceptance of Terms</h2>
            <p>
              By accessing or using GENI ("the Service"), you agree to be bound by these Terms of Use. 
              If you do not agree, you must not use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">2. Description of Service</h2>
            <p>
              GENI is a trading journal and analytics platform designed to help users track, analyze, 
              and improve trading performance. Features include trade logging, data-based analytics, 
              and optional image processing.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">3. Eligibility</h2>
            <p>
              You must be at least 18 years old to use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">4. User Accounts</h2>
            <p className="mb-3">
              You are responsible for maintaining the confidentiality of your account credentials 
              and for all activities under your account.
            </p>
            <p className="mb-2">You agree to:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Provide accurate and complete information</li>
              <li>Keep your login credentials secure</li>
              <li>Notify us immediately of any unauthorized use</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">5. Free Usage (Trial Model)</h2>
            <p className="mb-2">
              GENI may offer limited free usage based on a number of trades (e.g., first 10–20 trades).
            </p>
            <p className="mb-2">
              After reaching this limit, continued access to certain features may require payment.
            </p>
            <p>
              We reserve the right to modify or remove free usage limits at any time.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">6. Payments & Access</h2>
            <p className="mb-3">
              GENI may offer paid plans that grant access to premium features for a fixed period.
            </p>
            <ul className="list-disc list-inside space-y-1 ml-2 mb-3">
              <li>Payments are one-time and non-recurring</li>
              <li>Access is granted for the duration of the purchased plan</li>
              <li>Access expires automatically at the end of the period</li>
              <li>GENI does not perform automatic renewals or recurring charges</li>
            </ul>
            <p className="mb-2">
              Users are responsible for purchasing continued access if they wish to maintain premium features.
            </p>
            <p>
              We reserve the right to change pricing at any time. Changes apply to future purchases only.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">7. No Refund Policy</h2>
            <p className="mb-2">
              All purchases are final.
            </p>
            <p>
              GENI does not provide refunds, except where required by applicable law.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">8. User Data</h2>
            <p className="mb-3">
              You retain ownership of your data, including trades, notes, and images.
            </p>
            <p className="mb-3">
              GENI does not require or rely on sensitive financial information such as total account balance, 
              capital size, or position sizing to operate its core analytics.
            </p>
            <p>
              By using GENI, you grant us a limited license to store, process, analyze, and display your data 
              solely for the purpose of operating and improving the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">9. Analytics Disclaimer</h2>
            <p className="mb-3">
              GENI provides analytics and insights based on user-provided data.
            </p>
            <p className="mb-2">You acknowledge that:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>All analytics are generated from your own data inputs</li>
              <li>Results may be inaccurate, incomplete, or misleading depending on data quality</li>
              <li>Outputs are provided for informational purposes only</li>
              <li>They must not be relied upon as a basis for trading decisions</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">10. Image Processing (AI Use)</h2>
            <p className="mb-3">
              GENI may use artificial intelligence tools solely to process uploaded chart images and 
              extract structured information.
            </p>
            <p className="mb-2">You acknowledge that:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Image processing results may be inaccurate or incomplete</li>
              <li>This functionality is intended to assist data entry only</li>
              <li>It does not constitute financial advice or analysis</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">11. Financial Disclaimer</h2>
            <p className="mb-3">
              GENI does not provide financial advice, investment recommendations, or trading signals.
            </p>
            <p className="mb-2">Trading involves substantial risk, including:</p>
            <ul className="list-disc list-inside space-y-1 ml-2 mb-3">
              <li>Loss of all invested capital</li>
              <li>Potential losses exceeding your initial investment</li>
            </ul>
            <p>
              You are solely responsible for all trading decisions and outcomes.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">12. No Warranty</h2>
            <p className="mb-2">
              The Service is provided "as is" and "as available" without warranties of any kind.
            </p>
            <p>
              We do not guarantee that the Service will be uninterrupted, error-free, or accurate.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">13. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, GENI shall not be liable for any indirect, incidental, 
              special, or consequential damages, including but not limited to trading losses or data loss.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">14. Prohibited Uses</h2>
            <p className="mb-2">You agree not to:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Use the Service for illegal purposes</li>
              <li>Attempt unauthorized access</li>
              <li>Reverse engineer, copy, or exploit the platform</li>
              <li>Resell or redistribute the Service</li>
              <li>Upload malicious content</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">15. Termination</h2>
            <p>
              We reserve the right to suspend or terminate your account at any time if you violate 
              these Terms or misuse the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">16. Changes to Terms</h2>
            <p>
              We may update these Terms at any time. Continued use of the Service after changes 
              constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">17. Governing Framework</h2>
            <p>
              These Terms shall be interpreted and enforced in accordance with generally accepted 
              international legal principles applicable to digital services.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">18. Contact</h2>
            <p>
              If you have any questions about these Terms, please contact us through the app.
            </p>
          </section>

        </div>
      </div>
    </div>
  );
}
